import React, { useState, useMemo } from "react";
import { getAvatarColor, getInitials, truncateText, stripHtml } from "../../../utils/avatarUtils";
import "./NotificationCard.scss";

const MESSAGE_MAX_LEN = 35;

// Time: display as-is from API (backend sends formatted datetime)
// Message/body may be HTML from API; we render it with dangerouslySetInnerHTML (preview = plain text, expanded = HTML).

const NotificationCard = ({
  notifications = [],
  onMarkAsRead,
  onRowClick,
}) => {
  const [expandedId, setExpandedId] = useState(null);

  const notificationsList = useMemo(
    () => (Array.isArray(notifications) ? notifications : []),
    [notifications]
  );
  const unreadOnly = useMemo(
    () => notificationsList.filter((n) => !n.isRead),
    [notificationsList]
  );
  const unreadCount = unreadOnly.length;

  const toggleMessage = (e, id) => {
    e.stopPropagation();
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleRowClick = (notification) => {
    if (onRowClick) onRowClick(notification);
  };

  const hasData = unreadCount > 0;
  const isRowClickable = Boolean(onRowClick);

  return (
    <div className="notification-card">
      <div className="notification-card__header">
        <span className="notification-card__title">Notifications</span>
        <span className="notification-card__count" aria-label={`${unreadCount} unread`}>
          {unreadCount}
        </span>
      </div>

      {!hasData ? (
        <div className="notification-card__empty">No unread notifications</div>
      ) : (
        <div className="notification-card__table-wrap">
          <table className="notification-card__table">
            <thead>
              <tr>
                <th className="notification-card__col-from" aria-label="From">
                  From
                </th>
                <th className="notification-card__col-message">Message</th>
                <th className="notification-card__col-time">Date & Time</th>
                <th className="notification-card__col-action" aria-label="Mark as read" />
              </tr>
            </thead>
            <tbody>
              {unreadOnly.map((row) => {
                const rawMessage = row.message ?? "";
                const plainMessage = stripHtml(rawMessage);
                return (
                <tr
                  key={row.id}
                  className={isRowClickable ? "notification-card__row--clickable" : ""}
                  onClick={isRowClickable ? () => handleRowClick(row) : undefined}
                >
                  <td className="notification-card__col-from">
                    <span
                      className="notification-card__avatar"
                      style={{
                        backgroundColor: getAvatarColor(
                          row.senderName || rawMessage
                        ),
                      }}
                      title={row.senderName || "—"}
                    >
                      {getInitials(row.senderName || rawMessage || "?")}
                    </span>
                  </td>
                  <td
                    className={`notification-card__col-message notification-card__preview-cell ${
                      expandedId === row.id
                        ? "notification-card__preview-cell--expanded"
                        : ""
                    }`}
                    title={plainMessage}
                  >
                    {expandedId === row.id ? (
                      <span
                        className="notification-card__preview notification-card__preview--html"
                        dangerouslySetInnerHTML={{
                          __html: rawMessage || "—",
                        }}
                      />
                    ) : (
                      <span className="notification-card__preview notification-card__preview--plain">
                        {truncateText(plainMessage || "—", MESSAGE_MAX_LEN)}
                      </span>
                    )}
                    {plainMessage && plainMessage.length > MESSAGE_MAX_LEN && (
                      <button
                        type="button"
                        className="notification-card__see-more"
                        onClick={(e) => toggleMessage(e, row.id)}
                      >
                        {expandedId === row.id ? "[see less]" : "[see more]"}
                      </button>
                    )}
                  </td>
                  <td className="notification-card__col-time">
                    <span
                      className="notification-card__truncate"
                      title={row.timestamp}
                    >
                      {row.timestamp}
                    </span>
                  </td>
                  <td className="notification-card__col-action">
                    {onMarkAsRead && (
                      <button
                        type="button"
                        className="notification-card__mark-read-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(row.id);
                        }}
                        title="Mark as read"
                        aria-label="Mark as read"
                      >
                        <i className="fa fa-check-circle" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationCard;
